import { SelectHTMLAttributes, forwardRef } from 'react';
import './Form.css';

export type SelectSize = 'sm' | 'md' | 'lg';

export interface SelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'size'
> {
  /** Size variant */
  size?: SelectSize;
  /** Error state */
  error?: boolean;
  /** Full width select */
  fullWidth?: boolean;
}

/**
 * Select component for dropdown selection.
 *
 * @example
 * ```tsx
 * <Select value={status} onChange={(e) => setStatus(e.target.value)}>
 *   <option value="">All Statuses</option>
 *   <option value="active">Active</option>
 *   <option value="inactive">Inactive</option>
 * </Select>
 * ```
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      size = 'md',
      error = false,
      fullWidth = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const classNames = [
      'ui-select',
      `ui-select--${size}`,
      error && 'ui-select--error',
      fullWidth && 'ui-select--full-width',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <select ref={ref} className={classNames} {...props}>
        {children}
      </select>
    );
  }
);

Select.displayName = 'Select';
